import { passwordProblem } from '@quickreport/domain';
import { Button, Field, PasswordInput } from '@quickreport/ui';
import { useEffect, useState } from 'react';
import { api, describeError } from '../../lib/api';

/**
 * Choose or change the password used to sign in without a code. A code always works too, so a
 * forgotten password is never a lock-out.
 */
export function PasswordPanel() {
  const [has, setHas] = useState<boolean | null>(null);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    api<{ hasPassword: boolean }>('GET', '/v1/me')
      .then((m) => setHas(m.hasPassword))
      .catch(() => setHas(null));
  }, []);

  const problem = passwordProblem(next);

  async function save() {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setMessage(null);
    try {
      await api('POST', '/v1/me/password', {
        password: next,
        ...(current ? { currentPassword: current } : {}),
      });
      setHas(true);
      setCurrent('');
      setNext('');
      setTried(false);
      setMessage({
        tone: 'ok',
        text: 'Password saved. Next time you can sign in with it instead of a code.',
      });
    } catch (err) {
      setMessage({ tone: 'error', text: describeError(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="grid gap-3">
      <h2 className="text-base font-semibold">Password</h2>
      <p className="text-[13px] text-slate">
        {has
          ? 'You have a password. Sign in with it, or ask for a code instead, whichever is easier.'
          : 'You sign in with a code each time. Choose a password if you would rather type that.'}
      </p>
      <form
        className="grid max-w-sm gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {/* Lets a password manager notice the change. */}
        <input type="text" autoComplete="username" className="sr-only" tabIndex={-1} readOnly />
        {has ? (
          <Field
            label="Current password"
            hint="Not needed if you signed in a few minutes ago. Forgot it? Sign out and sign in with a code, then choose a new one."
          >
            <PasswordInput
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label={has ? 'New password' : 'Choose a password'} error={tried ? problem : null}>
          <PasswordInput
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
        </Field>
        <div>
          <Button type="submit" disabled={busy || !next}>
            {busy ? 'Saving…' : has ? 'Change password' : 'Save password'}
          </Button>
        </div>
      </form>
      {message ? (
        <p
          className={`rounded-md border px-3 py-2 text-[13px] ${message.tone === 'ok' ? 'border-green-200 bg-green-50 text-ok' : 'border-red-200 bg-red-50 text-flag'}`}
          role={message.tone === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </p>
      ) : null}
    </section>
  );
}
